/**
 * Webhook URL comparison helpers.
 *
 * Ownership of an incoming-sync webhook is decided by the per-user `t=` token
 * in the callback URL. Every module builds that URL by concatenating the token
 * raw (`UA_APP_UTILITY.getAuthorizedWebhookURL`), but some providers hand the
 * URL back percent-encoded, so a token holding base64 characters is stored as
 * `t=tok%2Ben%3D` and rebuilt as `t=tok+en=` — the same token, two strings.
 *
 * Decode a stored URL before comparing it against a rebuilt one, and decode a
 * token pulled out of a stored URL before looking the user up by it.
 *
 * Decoding is safe in both directions because an issued token never contains a
 * `%`: a raw stored token decodes to itself, an encoded one decodes back to the
 * original, so both spellings normalise to the same value. A token that could
 * hold a literal `%` would break that — `t=lit%2Blit` would decode to `lit+lit`.
 */

/**
 * Percent-decode a webhook URL so two spellings of the same token compare
 * equal. Returns the URL unchanged if it holds a malformed escape.
 */
export function decodeWebhookUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    return decodeURIComponent(url);
  } catch {
    return url;
  }
}

/**
 * Pull the `t=` token out of a webhook URL and decode it, so the value matches
 * the token as issued rather than as the provider spelled it back. Returns null
 * when the URL carries no token.
 */
export function extractWebhookToken(url: string | null | undefined): string | null {
  if (!url) return null;
  const raw = url.match(/[?&]t=([^&]+)/)?.[1];
  if (!raw) return null;
  return decodeWebhookUrl(raw);
}
